/**
 * Boundary-shelf setups on the TrendPulse curve, scored against price and drawn as vertical lines.
 * Part of the OPERATOR FEATURES set — see operatorFeatures.js for how to turn it off.
 *
 * <h2>What is being validated</h2>
 *
 * A two-point flat shelf on the curve — level within 0.75% of its 25-bar range — that is <em>flanked
 * on both sides</em> by moves of at least 4% of that range. Momentum in, nothing, momentum out. A
 * shelf at the bottom of the corridor after a fall is a long setup; a shelf at the top after a rise
 * is a short one. Each is then scored on what price actually did over the next five bars, against a
 * target of one ATR(14) and a stop at the shelf bar's own opposite extreme.
 *
 * <h2>This is a RETROSPECTIVE tool, and the outgoing slope is why</h2>
 *
 * The shelf is only a shelf once the curve has left it, so the pattern at bar T cannot be known
 * until T+1. Every line this draws is therefore a verdict on history, not a signal that could have
 * been taken at the time. That is the right shape for a validation pass — the question is "does the
 * shape work", not "what should I do now" — but it means these lines must never be read as entries.
 * The entry detector in trendPulseEntries.js is the one with no look-ahead; this is deliberately a
 * different question, so the two are separate files rather than one with a flag.
 *
 * <h2>First touch, and a tie goes against the setup</h2>
 *
 * The walk forward stops at whichever level is reached first. A bar that spans both the target and
 * the stop is scored a failure: at daily resolution the path inside the bar is unknown, and
 * assuming the favourable order is how a backtest flatters itself. A setup that reaches neither in
 * five bars is UNRESOLVED and draws no line — the study defines a green line and a red line, and
 * inventing a third colour for "nothing happened" would be adding a claim it does not make.
 */

/** Price rows are [open, close, low, high] — NOT OHLC order. */
export const OPEN = 0;
export const CLOSE = 1;
export const LOW = 2;
export const HIGH = 3;

export const LONG = 'long';
export const SHORT = 'short';

export const SUCCESS = 'success';
export const FAILURE = 'failure';
export const UNRESOLVED = 'unresolved';

/** The shelf geometry, as the study specifies it. */
export const SHELF_WINDOW = 25;
export const SHELF_FLAT_PCT = 0.0075;
export const SHELF_SLOPE_PCT = 0.04;

/** The outcome test. */
export const ATR_PERIOD = 14;
export const ATR_MULTIPLE = 1.0;
export const EVAL_HORIZON = 5;

export const LINE_COLORS = { [SUCCESS]: '#00c853', [FAILURE]: '#d500f9' };

/**
 * Wilder's ATR, one value per bar, null until the period is full.
 *
 * <p>Wilder's smoothing rather than a plain mean, because that is what ATR(14) means everywhere
 * else it is quoted — a simple average of the last fourteen true ranges is a different and
 * consistently smaller number, so a target set from it would not be the one-ATR target the study
 * asks for.
 *
 * <p>The first true range has no previous close and is the bar's own range; every later one is the
 * greatest of the bar's range and its two gaps from the previous close.
 */
export function atr(ohlc, period = ATR_PERIOD) {
  const bars = Array.isArray(ohlc) ? ohlc : [];
  const out = new Array(bars.length).fill(null);
  if (bars.length < period) return out;

  const trueRanges = bars.map((bar, i) => {
    if (!Array.isArray(bar)) return null;
    const [h, l] = [bar[HIGH], bar[LOW]];
    if (!Number.isFinite(h) || !Number.isFinite(l)) return null;
    const prev = i > 0 && Array.isArray(bars[i - 1]) ? bars[i - 1][CLOSE] : null;
    if (!Number.isFinite(prev)) return h - l;
    return Math.max(h - l, Math.abs(h - prev), Math.abs(l - prev));
  });

  let sum = 0;
  for (let i = 0; i < period; i++) {
    if (!Number.isFinite(trueRanges[i])) return out;   // a gap early on: no ATR at all
    sum += trueRanges[i];
  }
  let value = sum / period;
  out[period - 1] = value;
  for (let i = period; i < bars.length; i++) {
    const tr = trueRanges[i];
    if (!Number.isFinite(tr)) { out[i] = value; continue; }
    value = ((value * (period - 1)) + tr) / period;
    out[i] = value;
  }
  return out;
}

/** The curve's range over the trailing window, and this bar's position inside it. */
export function shelfScale(values, t, window = SHELF_WINDOW) {
  if (!Array.isArray(values) || t < window - 1) return null;
  let min = Infinity;
  let max = -Infinity;
  for (let i = t - window + 1; i <= t; i++) {
    const v = values[i];
    if (!Number.isFinite(v)) return null;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  const range = max - min;
  if (!(range > 0)) return null;
  return { range, pct: (values[t] - min) / range };
}

/**
 * Every boundary shelf: flat within ε, flanked both sides, and at the corridor edge the direction
 * implies.
 *
 * <p>Both halves of "incoming slope down, shelf at bottom" are enforced. The slope alone would
 * accept a shelf halfway up the corridor, which is a pause in a move rather than a boundary; the
 * position alone would accept a shelf the curve drifted into. The study names both, so both are
 * required — and it is worth knowing that this is what makes the detector selective, because on
 * real data the pair is far rarer than either.
 */
export function detectBoundaryShelves(values, {
  window = SHELF_WINDOW, flatPct = SHELF_FLAT_PCT, slopePct = SHELF_SLOPE_PCT } = {}) {
  const out = [];
  const list = Array.isArray(values) ? values : [];
  // Stops one short of the end: the outgoing slope needs the bar after the shelf.
  for (let t = 2; t < list.length - 1; t++) {
    const scale = shelfScale(list, t, window);
    if (!scale) continue;
    const [before, mid, now, after] = [list[t - 2], list[t - 1], list[t], list[t + 1]];
    if (![before, mid, now, after].every(Number.isFinite)) continue;

    const flat = flatPct * scale.range;
    const steep = slopePct * scale.range;
    if (Math.abs(now - mid) > flat) continue;                 // the two-point shelf
    const incoming = mid - before;
    const outgoing = after - now;
    if (Math.abs(incoming) < steep || Math.abs(outgoing) < steep) continue;

    const direction = incoming < 0 ? LONG : SHORT;
    // "shelf at bottom" / "shelf at top", on the same quartiles the phase ribbon uses.
    if (direction === LONG && scale.pct > 0.25) continue;
    if (direction === SHORT && scale.pct < 0.75) continue;

    out.push({
      index: t,
      direction,
      points: [t - 2, t - 1, t, t + 1],
      corridorPct: scale.pct,
      incoming,
      outgoing,
      detail: `${direction === LONG ? 'Long' : 'Short'} boundary shelf at the `
        + `${(scale.pct * 100).toFixed(0)}th percentile — in ${Math.abs(incoming).toFixed(2)}, `
        + `flat ${Math.abs(now - mid).toFixed(2)}, out ${Math.abs(outgoing).toFixed(2)} `
        + `(ε=${flat.toFixed(2)}, slope=${steep.toFixed(2)})`,
    });
  }
  return out;
}

/**
 * What price did over the next bars: one ATR in the setup's favour, or the shelf bar's other
 * extreme against it, whichever comes first.
 */
export function evaluateSetup(ohlc, atrSeries, shelf, {
  horizon = EVAL_HORIZON, multiple = ATR_MULTIPLE } = {}) {
  const bars = Array.isArray(ohlc) ? ohlc : [];
  const t = shelf?.index;
  const anchor = bars[t];
  const a = Array.isArray(atrSeries) ? atrSeries[t] : null;
  if (!Array.isArray(anchor) || !Number.isFinite(a)) return null;

  const long = shelf.direction === LONG;
  const target = long ? anchor[HIGH] + multiple * a : anchor[LOW] - multiple * a;
  const stop = long ? anchor[LOW] : anchor[HIGH];

  const last = Math.min(t + horizon, bars.length - 1);
  for (let i = t + 1; i <= last; i++) {
    const bar = bars[i];
    if (!Array.isArray(bar)) continue;
    const hitTarget = long ? bar[HIGH] >= target : bar[LOW] <= target;
    const hitStop = long ? bar[LOW] < stop : bar[HIGH] > stop;
    // Both inside one bar: the path within it is unknown at this resolution, so it is scored
    // against the setup. Assuming the favourable order is how a backtest flatters itself.
    if (hitTarget && hitStop) return { ...shelf, outcome: FAILURE, resolvedAt: i, target, stop, atr: a, ambiguous: true };
    if (hitTarget) return { ...shelf, outcome: SUCCESS, resolvedAt: i, target, stop, atr: a };
    if (hitStop) return { ...shelf, outcome: FAILURE, resolvedAt: i, target, stop, atr: a };
  }
  // Neither level reached, or the horizon runs past the data. The study defines a green line and a
  // red line and nothing else, so this draws nothing rather than inventing a third verdict.
  return { ...shelf, outcome: UNRESOLVED, target, stop, atr: a, truncated: last < t + horizon };
}

/** Every shelf on the curve, scored. */
export function evaluateBoundaryShelves(values, ohlc, opts = {}) {
  const atrSeries = atr(ohlc, opts.atrPeriod || ATR_PERIOD);
  return detectBoundaryShelves(values, opts)
    .map(s => evaluateSetup(ohlc, atrSeries, s, opts))
    .filter(Boolean);
}

/**
 * One vertical line per resolved setup, spanning every pane.
 *
 * <p>Emitted as ordinary `vLine` overlays rather than as a new overlay type, because the chart
 * already draws those into the price grid AND the indicator grid — "across all panes" is a
 * property this codebase already has, and a second implementation of it would be a second thing to
 * keep in step.
 *
 * <p>Unresolved setups produce no line at all. They are returned in the summary so the count can be
 * reported, because "18 lines from 30 setups" and "18 lines from 18 setups" say different things
 * about the detector.
 */
export const VALIDATION_LINE_PREFIX = 'validation-line-';

export function buildValidationLineOverlays(evaluated, barData) {
  const dates = barData && Array.isArray(barData.dates) ? barData.dates : [];
  return (Array.isArray(evaluated) ? evaluated : [])
    .filter(e => e.outcome === SUCCESS || e.outcome === FAILURE)
    .filter(e => dates[e.index] !== undefined)
    .map(e => ({
      id: `${VALIDATION_LINE_PREFIX}${e.index}`,
      type: 'vLine',
      date: dates[e.index],
      color: LINE_COLORS[e.outcome],
      thickness: 2,
      // Dashed for a failure: colour carries the verdict and so does the stroke, so the two survive
      // a monochrome screenshot and a reader who cannot separate green from magenta.
      lineType: e.outcome === SUCCESS ? 'solid' : 'dashed',
      label: `${e.direction === LONG ? 'L' : 'S'} ${e.outcome === SUCCESS ? '✓' : '✗'}`,
      customLabelColor: LINE_COLORS[e.outcome],
    }));
}

/** Counts by direction and outcome, for the status line. */
export function summariseValidation(evaluated) {
  const list = Array.isArray(evaluated) ? evaluated : [];
  const by = (d, o) => list.filter(e => e.direction === d && e.outcome === o).length;
  const resolved = list.filter(e => e.outcome !== UNRESOLVED);
  const wins = resolved.filter(e => e.outcome === SUCCESS).length;
  return {
    total: list.length,
    drawn: resolved.length,
    unresolved: list.length - resolved.length,
    longSuccess: by(LONG, SUCCESS),
    longFailure: by(LONG, FAILURE),
    shortSuccess: by(SHORT, SUCCESS),
    shortFailure: by(SHORT, FAILURE),
    successRate: resolved.length ? (100 * wins) / resolved.length : null,
  };
}
